import { Drawer, Grid, InputAdornment, MenuItem, Tab, Tabs, TextField } from "@mui/material";
import moment from 'moment';
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { EventStatusEnum } from "../../_common/enum/forms/eventStatus.enum";
import { IEventManage } from "../../_common/interface/event/iEventManage";
import { LicenseGradeMultipleAutoComplete } from "../../_components/autocomplete/licenseGrade.multipleComplete";
import { OrganiserMultipleAutoComplete } from "../../_components/autocomplete/organiser.multipleComplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { TinyMce } from "../../_components/tinyMce/tinyMce";
import { toastr } from "../../_components/toastr/toastr";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { accountService } from "../../_services/accounts.service";
import { eventService } from "../../_services/event.service";
import { useDrawerStyles } from "../../_styles/drawer.styles";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { RacingClubAutoComplete } from "../../_components/autocomplete/racingClub.autoComplete";


export const EventManageDrawer = (params: { emitOnSave?(): void, emitOnClose?(): void }) => {

    const { eventId } = useParams<{ eventId: string }>();
    const _eventId = Number(eventId);

    const resetData = () => ({
        id: 0,
        name: "",
        description: "",
        eventDate: "",
        price: 0,
        eventOrganisers: [],
        eventRacingLicenses: [],
        racingClubId: 0,
        eventStatusId: EventStatusEnum.New
    });

    const [data, setData] = useState<IEventManage>(resetData);
    const [tabValue, setTabValue] = useState(0);

    let navigate = useNavigate();
    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        if (eventId != null)
            fetchData();
        else {
            setData(resetData);
        }
    }, [eventId]);

    useEffect(() => {
        if (data.eventDate === "") return;
        setData((_data: IEventManage) => {
            return {
                ..._data, eventDate: moment(data.eventDate).format('YYYY-MM-DD')
            }
        });


    }, [data.eventDate]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);


    function fetchData() {
        if (!eventId) return;

        setIsLoading(true)
        return eventService
            .get(_eventId)
            .then(
                (json) => {
                    if (json && json.eventRacingLicenses && json.eventRacingLicenses.length) {
                        json.eventRacingLicenses = json.eventRacingLicenses.map((e: any) => ({
                            id: e.racingLicenseId,
                        }));
                    }

                    if (json && json.eventOrganisers && json.eventOrganisers.length) {
                        json.eventOrganisers = json.eventOrganisers.map((e: any) => ({
                            id: e.userId,
                        }));
                    }
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);


        var _data = { ...data };
        if (_data.eventRacingLicenses) {
            _data.eventRacingLicenses.forEach(erl => {
                erl.racingLicenseId = erl.id;
                erl.eventId = _data.id;
                erl.id = 0;
            });
        }

        if (_data.eventOrganisers) {
            _data.eventOrganisers.forEach(eo => {
                eo.userId = eo.id;
                eo.eventId = _data.id;
                eo.id = 0;
            });
        }

        return eventService
            .submit(data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    if (params.emitOnSave) params.emitOnSave();
                },
                handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!eventId) return;
        setIsLoading(true);
        return eventService
            .disable(_eventId)
            .then(
                (d) => {
                    toastr.success("Disabled successfully");
                    fetchData();
                    if (params.emitOnSave) params.emitOnSave();
                },
                (e) => {
                    toastr.error(e);
                }
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data) => ({ ..._data, [event.target.name]: event.target.value }))
    };


    function handleTextareaChange(newContent: string) {
        setData((_data) => ({ ..._data, description: newContent }))
    };


    return (
        <Drawer
            anchor={"right"}
            open={true}>

            <ValidatorForm
                onSubmit={submit}
                noValidate>

                <DrawerHeader name={(!!eventId) ? "Update Event" : "Create Event"} onClose={() => { if (params.emitOnClose) params.emitOnClose() }} />

                {(_eventId > 0) &&
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="simple tabs example">
                        <Tab label="Event" onClick={() => { navigate("") }} />
                        {accountService.isAdmin && <Tab label="Manage Documents" onClick={() => { navigate("ManageDocuments") }} />}
                        <Tab label="Documents Upload" onClick={() => { navigate("EventDocuments") }} />
                    </Tabs>
                }
                {(tabValue === 0) &&
                    <Grid container spacing={1} sx={useDrawerStyles.container}>

                        <Grid item xs={12}>

                            <RacingClubAutoComplete
                                isRequired
                                itemId={data.racingClubId}
                                name={"racingClubId"}
                                onItemChange={handleChange} />
                        </Grid>

                        <Grid item xs={12}>
                            <TextValidatorHcs
                                key="name"
                                name="name"
                                label="Name"
                                required
                                fullWidth
                                validators={['required', 'minStringLength:1']}
                                errorMessages={['required field', "Field cannot be empty"]}
                                value={data.name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TinyMce tagName="Description" value={data.description} onChange={handleTextareaChange} />
                        </Grid>

                        <Grid item xs={12}>
                            <TextValidatorHcs
                                type="date"
                                key="eventDate"
                                name="eventDate"
                                label="Event Date"
                                required
                                fullWidth
                                validators={['required', 'minStringLength:1']}
                                errorMessages={['required field', "Field cannot be empty"]}
                                value={data.eventDate}
                                onChange={handleChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextValidatorHcs
                                type="number"
                                key="price"
                                name="price"
                                label="Price"
                                required
                                fullWidth
                                validators={['required']}
                                errorMessages={['required field']}
                                InputProps={{
                                    startAdornment: data.price ? <InputAdornment position="start">{"\u20AC"}</InputAdornment> : null,
                                }}
                                value={data.price}
                                onChange={handleChange}
                                autoFocus
                                InputLabelProps={{
                                    shrink: !data.price,
                                }}
                            />
                        </Grid>

                        {accountService.isAdmin &&
                            <Grid item xs={12}>

                                <OrganiserMultipleAutoComplete
                                    isRequired
                                    // groupedBy={"formFieldKeyShortName"}
                                    name={"eventOrganisers"}
                                    items={data.eventOrganisers}
                                    onItemChange={handleChange} />
                            </Grid>
                        }

                        <Grid item xs={12}>

                            <LicenseGradeMultipleAutoComplete
                                isRequired
                                // groupedBy={"formFieldKeyShortName"}
                                name={"eventRacingLicenses"}
                                items={data.eventRacingLicenses}
                                onItemChange={handleChange} />
                        </Grid>

                        {accountService.isAdmin &&

                            <Grid item xs={12}>

                                <TextField
                                    label="Event Status"
                                    name="eventStatusId"
                                    value={data.eventStatusId}
                                    placeholder="Event Status"
                                    onChange={handleChange}
                                    variant="outlined"
                                    select
                                    helperText={data.eventStatusId == EventStatusEnum.New ? "Only visible to selected organisers." : data.eventStatusId == EventStatusEnum.Visible ? "Viewable, but users will not be able to join until made active." : data.eventStatusId == EventStatusEnum.Active ? "Users are able to join" : ""}
                                    fullWidth
                                >
                                    <MenuItem value={EventStatusEnum.New}>New</MenuItem>
                                    <MenuItem value={EventStatusEnum.Visible}>Visible</MenuItem>
                                    <MenuItem value={EventStatusEnum.Active}>Active</MenuItem>
                                </TextField>

                            </Grid>
                        }

                        <Grid item xs={(data.id === 0 ? 12 : 6)}>
                            <PrimaryButton
                                isSaving={isSaving}
                                isLoading={isLoading}
                                type="submit"
                                variant="save">{"Save"}
                            </PrimaryButton>
                        </Grid>


                        {data.id > 0 &&
                            <Grid item xs={6}>
                                <PrimaryButton
                                    type="button"
                                    variant="danger"
                                    disabled={isLoading}
                                    onClick={disable}
                                    icon="block"
                                >
                                    Disable
                                </PrimaryButton>
                            </Grid>
                        }
                    </Grid>
                }

            </ValidatorForm>

            {(tabValue > 0) &&
                <Outlet />
            }
        </Drawer>
    );
};